import { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import styles from './MapPicker.module.css';

// Fix default marker icons for Vite/bundlers
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';

delete (L.Icon.Default.prototype as unknown as Record<string, unknown>)._getIconUrl;
L.Icon.Default.mergeOptions({ iconUrl, iconRetinaUrl, shadowUrl });

const DEFAULT_CENTER: [number, number] = [44.0, 17.5]; // Bosnia
const DEFAULT_ZOOM = 6;
const PIN_ZOOM = 13;

interface Props {
  lat?: number | null;
  lng?: number | null;
  onChange: (lat: number, lng: number) => void;
}

// Recenter the map when position changes (MapContainer ignores prop updates)
const RecenterMap = ({ position }: { position: [number, number] }) => {
  const map = useMap();
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    map.setView(position, PIN_ZOOM);
  }, [position, map]);
  return null;
};

// Handles click-to-place and drag events
const LocationMarker = ({
  position,
  onChange,
}: {
  position: [number, number];
  onChange: (lat: number, lng: number) => void;
}) => {
  const markerRef = useRef<L.Marker>(null);

  useMapEvents({
    click(e) {
      onChange(e.latlng.lat, e.latlng.lng);
    },
  });

  return (
    <Marker
      ref={markerRef}
      position={position}
      draggable
      eventHandlers={{
        dragend() {
          const pos = markerRef.current?.getLatLng();
          if (pos) onChange(pos.lat, pos.lng);
        },
      }}
    />
  );
};

const MapPicker = ({ lat, lng, onChange }: Props) => {
  const hasPin = lat != null && lng != null;
  const position: [number, number] = hasPin ? [lat!, lng!] : DEFAULT_CENTER;
  const initialCenter: [number, number] = hasPin ? [lat!, lng!] : DEFAULT_CENTER;
  const initialZoom = hasPin ? PIN_ZOOM : DEFAULT_ZOOM;

  return (
    <div className={styles.wrapper}>
      <MapContainer
        center={initialCenter}
        zoom={initialZoom}
        className={styles.map}
        scrollWheelZoom
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {hasPin && <RecenterMap position={position} />}
        <LocationMarker position={position} onChange={onChange} />
      </MapContainer>
      <p className={styles.hint}>
        {hasPin
          ? `📍 ${lat!.toFixed(5)}, ${lng!.toFixed(5)} — drag pin or click to move`
          : 'Click on the map to set the project location'}
      </p>
    </div>
  );
};

export default MapPicker;
